import { useEffect } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ExternalRecipient } from '../features/externalRecipients/types';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';
import { filterPhoneInput, withInputFilter } from '../lib/inputFilters';

const ROLE_OPTIONS = [
  { value: 'empresa', label: 'Empresa' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'proveedor', label: 'Proveedor' },
  { value: 'otro', label: 'Otro' },
] as const;

const createSchema = z.object({
  firstName: z.string().min(1, 'El nombre es requerido'),
  lastName: z.string().min(1, 'El apellido es requerido'),
  email: z.string().email('Email inválido').min(1, 'Requerido'),
  phone: z.string().optional(),
  role: z.string().min(1, 'El rol es requerido'),
});

const editSchema = createSchema.extend({
  active: z.boolean().optional(),
});

type FormValues = z.infer<typeof editSchema>;

export type ExternalRecipientFormValues = FormValues;

interface Props {
  initial?: ExternalRecipient | null;
  loading?: boolean;
  error?: string;
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
}

function toFormValues(initial?: ExternalRecipient | null): FormValues {
  if (initial) {
    return {
      firstName: initial.firstName,
      lastName: initial.lastName,
      email: initial.email,
      phone: initial.phone || '',
      role: initial.role,
      active: initial.active,
    };
  }
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    active: true,
  };
}

export function ExternalRecipientForm({ initial, loading, error, onSubmit, onCancel }: Props) {
  const isEditing = !!initial;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(isEditing ? editSchema : createSchema) as Resolver<FormValues>,
    defaultValues: toFormValues(initial),
  });

  useEffect(() => {
    reset(toFormValues(initial));
  }, [initial, reset]);

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Nombre *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="text"
          placeholder="Nombre"
          {...register('firstName')}
        />
        {errors.firstName && (
          <p className="text-sm text-destructive">{errors.firstName.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Apellido *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="text"
          placeholder="Apellido"
          {...register('lastName')}
        />
        {errors.lastName && (
          <p className="text-sm text-destructive">{errors.lastName.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="email"
          placeholder="email@empresa.com"
          {...register('email')}
        />
        {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Teléfono</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="tel"
          inputMode="tel"
          placeholder="+5491112345678"
          {...withInputFilter(register('phone'), filterPhoneInput)}
        />
        {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Rol *</label>
        <select
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          {...register('role')}
        >
          <option value="">Selecciona un rol</option>
          {ROLE_OPTIONS.map((role) => (
            <option key={role.value} value={role.value}>
              {role.label}
            </option>
          ))}
        </select>
        {errors.role && <p className="text-sm text-destructive">{errors.role.message}</p>}
      </div>

      {isEditing && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="external-recipient-active"
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
            {...register('active')}
          />
          <label htmlFor="external-recipient-active" className="text-sm font-medium text-foreground">
            Activo
          </label>
        </div>
      )}

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
          {error}
        </div>
      )}

      <RequiredFieldsLegend />

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          className="rounded-md px-4 py-2 text-sm bg-secondary text-secondary-foreground hover:bg-secondary/80"
          onClick={onCancel}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="rounded-md px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {loading ? 'Guardando…' : isEditing ? 'Guardar' : 'Crear'}
        </button>
      </div>
    </form>
  );
}
